/**
 * The emoji shortcut bar and the full picker, as in the CRM chat window
 * (chat-composer-tools.js). The full list is the CRM's own emoji-data.js,
 * loaded on first open; shortcuts and recents are shared with the CRM chat.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { LoadingBlock } from '../components/Spinner';
import { CRM_FRAME_PATH } from '../crm/crmBridge';
import { DEFAULT_EMOJIS, emojiShortcuts, recentEmoji, saveEmojiShortcuts } from './composerPrefs';

interface EmojiGroup {
  group: string;
  items: [string, string][];
}

declare global {
  // eslint-disable-next-line no-var
  var CRM_EMOJI_GROUPS: EmojiGroup[] | undefined;
}

let loading: Promise<EmojiGroup[]> | null = null;

function loadEmojiGroups(): Promise<EmojiGroup[]> {
  if (globalThis.CRM_EMOJI_GROUPS) return Promise.resolve(globalThis.CRM_EMOJI_GROUPS);
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `${CRM_FRAME_PATH}chats/emoji-data.js?v=20261006-1`;
      s.onload = () => resolve(globalThis.CRM_EMOJI_GROUPS || []);
      s.onerror = () => {
        loading = null;
        reject(new Error('Emoji list failed to load'));
      };
      document.head.appendChild(s);
    });
  }
  return loading;
}

export function EmojiBar({ onPick, onOpenAll }: { onPick: (emoji: string) => void; onOpenAll: () => void }) {
  const { t } = useTranslation('chat');
  const list = emojiShortcuts();
  return (
    <div className="rc-emoji">
      {list.map((c) => (
        <button key={c} type="button" onClick={() => onPick(c)}>
          {c}
        </button>
      ))}
      <button type="button" className="rc-emoji-more" title={t('emoji.all')} onClick={onOpenAll}>
        <FeatherIcon name="plus" size={14} />
      </button>
    </div>
  );
}

interface PickerProps {
  onPick: (emoji: string) => void;
  onClose: () => void;
  /** Shortcut bar changed (Edit shortcuts saved). */
  onShortcutsChange: () => void;
}

export function EmojiPicker({ onPick, onClose, onShortcutsChange }: PickerProps) {
  const { t } = useTranslation('chat');
  const [groups, setGroups] = useState<EmojiGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let live = true;
    search.current?.focus();
    loadEmojiGroups()
      .then((g) => live && setGroups(g))
      .catch((err) => live && setError((err as Error).message));
    return () => {
      live = false;
    };
  }, []);

  const q = query.trim().toLowerCase();
  const recent = useMemo(() => (q ? [] : recentEmoji()), [q]);
  const shown = useMemo(
    () => (groups || []).map((g) => ({ ...g, items: q ? g.items.filter(([, name]) => name.includes(q)) : g.items })).filter((g) => g.items.length),
    [groups, q],
  );

  if (editing != null) {
    return (
      <div className="rc-pop rc-emoji-pop" role="dialog" aria-label={t('emoji.shortcuts')}>
        <div className="rc-pop-title">
          <b>{t('emoji.shortcuts')}</b>
          <span className="rc-muted">{t('emoji.shortcutsHint')}</span>
        </div>
        <textarea className="rc-pop-textarea" rows={3} aria-label={t('emoji.shortcuts')} value={editing} onChange={(e) => setEditing(e.target.value)} placeholder={DEFAULT_EMOJIS.slice(0, 4).join(' ')} />
        <div className="rc-pop-actions">
          <button type="button" className="rc-btn" onClick={() => setEditing(null)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="rc-btn rc-btn--primary"
            onClick={() => {
              saveEmojiShortcuts(editing.split(/\s+/).map((x) => x.trim()).filter(Boolean));
              onShortcutsChange();
              setEditing(null);
            }}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rc-pop rc-emoji-pop" role="dialog" aria-label={t('emoji.all')}>
      <div className="rc-pop-head">
        <FeatherIcon name="search" size={14} />
        <input ref={search} value={query} placeholder={t('emoji.search')} autoComplete="off" onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && onClose()} />
        <button type="button" className="rc-pop-link" onClick={() => setEditing(emojiShortcuts().join(' '))}>
          {t('emoji.editShortcuts')}
        </button>
        <button type="button" className="rc-pop-close" title={t('common.close')} onClick={onClose}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      <div className="rc-emoji-body">
        {!groups && !error && <LoadingBlock pad="16px 0" label={t('emoji.loading')} />}
        {error && <div className="rc-pop-empty">{error}</div>}
        {recent.length > 0 && (
          <>
            <div className="rc-emoji-group">{t('emoji.recent')}</div>
            <div className="rc-emoji-grid">
              {recent.map((e) => (
                <button key={e} type="button" onClick={() => onPick(e)}>
                  {e}
                </button>
              ))}
            </div>
          </>
        )}
        {shown.map((g) => (
          <div key={g.group}>
            <div className="rc-emoji-group">{g.group}</div>
            <div className="rc-emoji-grid">
              {g.items.map(([e, name]) => (
                <button key={e} type="button" title={name} onClick={() => onPick(e)}>
                  {e}
                </button>
              ))}
            </div>
          </div>
        ))}
        {groups && !shown.length && <div className="rc-pop-empty">{t('emoji.none')}</div>}
      </div>
    </div>
  );
}
